import React, { useEffect } from "react";
import styled, { css } from "styled-components";
import { useFormikContext } from "formik";
import { Alert, Col, ControlLabel, FormGroup } from "components/bootstrap";
import { FormikFormGroup, FormikInput, Link, Spinner } from "components/common";
import useSnapshotArchivingDefaults from "archive/hooks/useSnapshotArchivingDefaults";
import useSnapshotArchivingTypes from "archive/hooks/useSnapshotArchivingTypes";
import useDataTiering from "data-tiering/hooks/useDataTiering";
import DataTieringRoutes from "data-tiering/Routes";
import RepositoryManagement from "data-tiering/RepositoryManagement";
import WarmTierReadinessInfo from "data-tiering/WarmTierReadinessInfo";
import RepositoryAccessInfo from "data-tiering/repository-management/RepositoryAccessInfo";
import SnapshotFSPathFieldGroup from "./SnapshotFSPathFieldGroup";
const FIELD_PREFIX = "snapshot_archiving_config";
const TYPES_WITHOUT_SPOOLING = ["fs-1", "cloud-1"];
export const DEFAULT_SNAPSHOT_CONFIG = {
  type: null,
  repository_name: null,
  leader_only: true,
  spooling_directory: null
};
const StyledAlert = styled(Alert)(
  () => css`
    margin-top: 0;
  `
);
export const validateSnapshotArchivingFields = (config) => {
  const errors = {};
  if (!(config == null ? void 0 : config.repository_name)) {
    errors.repository_name = "Please select a snapshot repository.";
  }
  if ((config == null ? void 0 : config.repository_name) && !(config == null ? void 0 : config.type)) {
    errors.repository_name = "No snapshot archiving type is available for the selected repository.";
  }
  if (config && config.type !== "cloud-1" && config.spooling_directory && !config.spooling_directory.startsWith("/")) {
    errors.spooling_directory = "Must be an absolute path.";
  }
  return errors;
};
const SpoolingDirectoryFieldGroup = ({ placeholder }) => {
  const { setFieldValue } = useFormikContext();
  useEffect(
    () => () => {
      setFieldValue(`${FIELD_PREFIX}.spooling_directory`, void 0);
    },
    [setFieldValue]
  );
  return /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      name: `${FIELD_PREFIX}.spooling_directory`,
      type: "text",
      label: "Spooling Directory",
      placeholder,
      help: "Absolute path used to spool snapshot data while reading from cloud-backed repositories. Leave empty to use the server's snapshot-read spool setting.",
      labelClassName: "col-sm-3",
      wrapperClassName: "col-sm-9"
    }
  );
};
const SnapshotArchivingFields = ({
  repositories,
  credentialProblemRepositories,
  hasSupportedRepositories,
  isLoadingRepositories = false,
  disabled = false
}) => {
  var _a, _b, _c, _d, _e;
  const { values, errors, setFieldValue } = useFormikContext();
  const { readiness } = useDataTiering();
  const { data: snapshotTypes, isLoading: isLoadingTypes } = useSnapshotArchivingTypes();
  const { data: snapshotDefaults } = useSnapshotArchivingDefaults();
  const snapshotConfig = values.snapshot_archiving_config;
  const selectedRepoName = (_a = snapshotConfig == null ? void 0 : snapshotConfig.repository_name) != null ? _a : null;
  const supportedRepositoryTypes = (snapshotTypes != null ? snapshotTypes : []).map((t) => t.repository_type);
  const selectedRepository = (_b = repositories.find((entry) => entry.repository.name === selectedRepoName)) != null ? _b : null;
  const derivedType = selectedRepository ? (_d = (_c = snapshotTypes == null ? void 0 : snapshotTypes.find((t) => t.repository_type === selectedRepository.repository.type)) == null ? void 0 : _c.type) != null ? _d : null : null;
  useEffect(() => {
    if (snapshotConfig && derivedType && snapshotConfig.type !== derivedType) {
      setFieldValue(`${FIELD_PREFIX}.type`, derivedType);
    }
  }, [snapshotConfig, derivedType, setFieldValue]);
  const selectedNotReady = (selectedRepository == null ? void 0 : selectedRepository.status) === "not_ready";
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Col, { md: 9, mdOffset: 3 }, /* @__PURE__ */ React.createElement(WarmTierReadinessInfo, null)), ((_e = readiness.data) == null ? void 0 : _e.ready) && (isLoadingTypes || isLoadingRepositories ? /* @__PURE__ */ React.createElement(Col, { md: 9, mdOffset: 3 }, /* @__PURE__ */ React.createElement(Spinner, { text: "Loading snapshot archiving types..." })) : /* @__PURE__ */ React.createElement(React.Fragment, null, typeof errors.snapshot_archiving_config === "string" && /* @__PURE__ */ React.createElement(Col, { md: 9, mdOffset: 3 }, /* @__PURE__ */ React.createElement(StyledAlert, { bsStyle: "danger" }, errors.snapshot_archiving_config)), !hasSupportedRepositories && /* @__PURE__ */ React.createElement(Col, { md: 9, mdOffset: 3 }, /* @__PURE__ */ React.createElement(StyledAlert, { bsStyle: "danger", title: "You need to set up a Warm Tier Repository first" }, "The Snapshot strategy stores archives in a Warm Tier repository, and none of a supported type exist yet. Set one up (or ask an administrator to set one up) on the", " ", /* @__PURE__ */ React.createElement(Link, { to: DataTieringRoutes.WARM_TIER_REPOSITORIES }, "Warm Tier Repositories page"), ".")), hasSupportedRepositories && credentialProblemRepositories.length > 0 && /* @__PURE__ */ React.createElement(Col, { md: 9, mdOffset: 3 }, /* @__PURE__ */ React.createElement(StyledAlert, { bsStyle: "danger", title: "You need to set credentials for ALL repos" }, "Snapshot Archiving may read from any warm tier repository, and these have missing or invalid credentials: ", credentialProblemRepositories.join(", "), ". Fix them (or ask an administrator to fix them) on the ", /* @__PURE__ */ React.createElement(Link, { to: DataTieringRoutes.WARM_TIER_REPOSITORIES }, "Warm Tier Repositories page"), ".")), hasSupportedRepositories && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Col, { md: 9, mdOffset: 3 }, /* @__PURE__ */ React.createElement(RepositoryAccessInfo, null)), /* @__PURE__ */ React.createElement(
    RepositoryManagement,
    {
      layout: "horizontal",
      disabled,
      fieldName: `${FIELD_PREFIX}.repository_name`,
      types: supportedRepositoryTypes,
      label: "Snapshot Repository",
      help: "Repository used to create snapshots of hot indices. Already-warm indices are read from their own backing repository. Legacy archive file system backends cannot be used here."
    }
  ), selectedNotReady && /* @__PURE__ */ React.createElement(Col, { md: 9, mdOffset: 3 }, /* @__PURE__ */ React.createElement(StyledAlert, { bsStyle: "danger" }, "The selected repository is not ready and cannot become the active Snapshot repository. Repair it on the ", /* @__PURE__ */ React.createElement(Link, { to: DataTieringRoutes.WARM_TIER_REPOSITORIES }, "Warm Tier Repositories page"), ".")), derivedType === "fs-1" && /* @__PURE__ */ React.createElement(SnapshotFSPathFieldGroup, null), derivedType && !TYPES_WITHOUT_SPOOLING.includes(derivedType) && /* @__PURE__ */ React.createElement(SpoolingDirectoryFieldGroup, { placeholder: snapshotDefaults == null ? void 0 : snapshotDefaults.spooling_directory }), /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(ControlLabel, { className: "col-sm-3" }, "Restrict Creation to Leader Node"), /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      name: `${FIELD_PREFIX}.leader_only`,
      id: "snapshot_leader_only",
      type: "checkbox",
      formGroupClassName: "col-sm-9",
      label: "Restrict archive creation jobs to run only on the leader node (recommended)",
      help: "Repository access is verified from the node handling this request and must also hold on the node performing archiving; restricting creation to the leader narrows that requirement to one node."
    }
  )), /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(ControlLabel, { className: "col-sm-3" }, "Processing Threads"), /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      name: `${FIELD_PREFIX}.processing_threads`,
      id: "snapshot_processing_threads",
      type: "number",
      min: 1,
      placeholder: "Automatic",
      formGroupClassName: "col-sm-9",
      label: "Threads converting one shard",
      help: "Start at about 25% of the archiving node's cores. More threads convert faster and load the node harder. A running job keeps its starting value."
    }
  ))))));
};
export default SnapshotArchivingFields;
